import type {
  File,
  Folder,
  FolderRetrieve,
  FolderCreateRequest,
  FolderUpdateRequest,
  PaginatedFolderList,
} from '@openapi'
import type { FolderListRequest } from '@sdk/payload.types'

/**
 * Retrieves a list of folders.
 * @param payload - The payload containing the search, ordering, filters, fields, name, page, and page_size.
 * @returns A promise that resolves to the list of folders.
 */
export function getFolders(payload: FolderListRequest): Promise<PaginatedFolderList> {
  return this.API.request('get_folders', payload)
}

/**
 * Retrieves a folder by its ID.
 * @param payload - An object containing the folder ID.
 * @param payload.id - The ID of the folder to retrieve. Defaults to 'root'.
 * @returns A promise that resolves to the retrieved folder.
 */
export function getFolder(payload: { id: FolderRetrieve['id'] } = { id: 'root' }): Promise<FolderRetrieve> {
  return this.API.request('get_folder', payload)
}

/**
 * Creates a new folder.
 * @param payload - The folder creation request payload, excluding the instance_id.
 * @returns A promise that resolves to the created folder.
 */
export function createFolder(payload: Omit<FolderCreateRequest, 'instance_id'>): Promise<FolderRetrieve> {
  return this.API.request('create_folder', payload)
}

type UpdateFolderPayload = FolderUpdateRequest & { id: Folder['id'] }

/**
 * Updates an existing folder.
 * @param payload - The folder update payload, including the folder ID and update data.
 * @returns A promise that resolves to the updated folder.
 */
export function updateFolder(payload: UpdateFolderPayload): Promise<FolderRetrieve> {
  return this.API.request('update_folder', payload)
}

/**
 * Deletes a folder.
 * @param payload - An object containing the ID of the folder to delete.
 * @param payload.folder_id - The ID of the folder to delete.
 * @returns A promise that resolves when the folder is deleted.
 */
export function deleteFolder(payload: { folder_id: FolderRetrieve['id'] }): Promise<void> {
  return this.API.request('delete_folder', payload)
}

/**
 * Moves items (files or folders) to a target folder.
 * @param payload - The payload containing the target folder ID and items to move.
 * @param payload.target_folder_id - The ID of the target folder.
 * @param payload.items - An array of items to move, each with an ID and type.
 * @returns A promise that resolves to the updated target folder.
 */
export function moveFolderItems(payload: {
  target_folder_id: Folder['id']
  items: { id: File['id'] | Folder['id']; type: 'folder' | 'file' }[]
}): Promise<FolderRetrieve> {
  return this.API.request('move_folder_items', payload)
}
